"use client";

import clsx from "clsx";
import Link from "next/link";
import type { MouseEvent } from "react";
import { useModals } from "@/components/modals/ModalProvider";
import type { AcfButton } from "@/components/page-builder/types";
import { hasButton } from "./text";

type Variant = "pill" | "dark" | "link" | "light-link";

type Props = {
  btn: AcfButton | false | null | undefined;
  /**
   * pill: the green pill (the default). dark: the same pill in das-black.
   * link: the label and arrow alone, which the partner section runs as a text link.
   * light-link: the same text link in white, for a dark or blue ground.
   */
  variant?: Variant;
};

// The rounder, lighter, smaller v2 pill; it shares no measurements with the older btn-das.
const BASE =
  "inline-flex items-center border-0 font-das leading-none text-center no-underline appearance-none transition-opacity duration-400 ease-[ease]";

const PILL =
  "py-[13px] px-7 rounded-[100px] text-[15px] font-semibold text-white hover:text-white hover:no-underline hover:opacity-80 mobile:py-3 mobile:px-6 mobile:text-[14px]";

const VARIANTS: Record<Variant, string> = {
  pill: `${PILL} bg-das-green`,
  dark: `${PILL} bg-das-black`,
  link: "p-0 rounded-none bg-transparent text-[17px] font-medium text-das-green hover:text-das-green hover:underline hover:opacity-100 mobile:p-0 mobile:text-[16px]",
  "light-link":
    "p-0 rounded-none bg-transparent text-[15px] font-semibold text-white hover:text-white hover:no-underline hover:opacity-75",
};

// The theme's .ico: an icon box whose text, if any, is pushed out of sight.
const ICO =
  "relative inline-block align-top text-[0px] leading-[0] indent-[-4004px] size-4 ml-2";

/**
 * The shared arrow is a white SVG, which disappears once the pill is gone, so
 * the link repaints it from its own colour through a mask instead.
 */
const ARROWS: Record<Variant, string> = {
  pill: "bg-[url(/theme/images/icons/arrow-right.svg)] bg-contain bg-center bg-no-repeat",
  dark: "bg-[url(/theme/images/icons/arrow-right.svg)] bg-contain bg-center bg-no-repeat",
  link: "bg-current bg-none mask-[url(/theme/images/icons/arrow-right.svg)] mask-contain mask-center mask-no-repeat",
  "light-link":
    "bg-[url(/theme/images/icons/arrow-right.svg)] bg-contain bg-center bg-no-repeat",
};

/**
 * The v2 sections' pill: the [COMP] Button group as a text with an arrow.
 *
 * Nothing renders without both a text and somewhere to go. A modal button
 * always has somewhere to go (the theme pointed it at "#"), and opens the
 * modal named in the field, e.g. signin, as "signin-modal".
 */
export default function PillButton({ btn, variant = "pill" }: Props) {
  const { openModal } = useModals();

  if (!hasButton(btn)) {
    return null;
  }

  const isModal = btn.type === "trigger_modal";
  const url = isModal ? "#" : String(btn.url);
  const className = clsx(BASE, VARIANTS[variant]);

  const content = (
    <>
      <span className="leading-none">{btn.text}</span>
      <i className={clsx(ICO, ARROWS[variant])}></i>
    </>
  );

  if (isModal) {
    const target = btn.modal ? `${btn.modal}-modal` : undefined;

    const open = (event: MouseEvent) => {
      event.preventDefault();

      if (target) {
        openModal(target);
      }
    };

    return (
      <a
        className={clsx(className, "js-trigger-modal")}
        href="#"
        data-target={target}
        onClick={open}
      >
        {content}
      </a>
    );
  }

  if (url.startsWith("/") && !url.startsWith("//")) {
    return (
      <Link className={className} href={url}>
        {content}
      </Link>
    );
  }

  return (
    <a className={className} href={url}>
      {content}
    </a>
  );
}
